import React, { useContext } from 'react';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';

import { BackstageLoggerDefaultArgs } from '../../constants';
import * as D from '../../deps';

const { AUDIO_PLAY_TRACK, PAUSE } = D.Constants.AudioPlayer.AudioPlayerActions;
const { SHARE } = D.Constants.SourceCard.SourceCardConstants;

export interface ICuratorButtonControlsStateProps {
  showPremiumAccess: ReturnType<typeof D.Utils.Listener.showPremiumAccess>;
}

export interface ICuratorButtonControlsDispatchProps {
  playResumeSource: typeof D.playResumeSource;
  pauseTrack: typeof D.pauseTrack;
  openModal: typeof D.openModal;
  closeModal: typeof D.closeModal;
  openShareModal: typeof D.openShareModal;
}

export interface ICuratorButtonControlsOwnProps {
  curatorPandoraId: string;
  playablePandoraId: string;
  shareableUrlPath: string;
  playableContentType: string;
}

export type ICuratorButtonControlsProps = ICuratorButtonControlsStateProps &
  ICuratorButtonControlsDispatchProps &
  ICuratorButtonControlsOwnProps;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useClickControls(props: ICuratorButtonControlsProps) {
  const { integration } = useContext(D.AppContext);
  const {
    curatorPandoraId,
    playablePandoraId,
    playableContentType,
    shareableUrlPath,
    showPremiumAccess,
    playResumeSource,
    pauseTrack,
    openShareModal,
  } = props;

  const logClickEvent = (componentEventType: $TSFixMe, pandoraId: string): void => {
    integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType },
    });
  };

  const playOnClick = (): void => {
    logClickEvent(AUDIO_PLAY_TRACK, playablePandoraId);
    // TODO: WEBCA-2934 investigate why we're only passing these props through
    // to PLAY_RESUME_SOURCE.
    // @ts-expect-error
    playResumeSource({
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      forceRestartSourceIfPlaying: showPremiumAccess,
      id: playablePandoraId,
      pageUrl: shareableUrlPath,
      pandoraType: playableContentType,
    });
  };

  const pauseOnClick = (): void => {
    logClickEvent(PAUSE, playablePandoraId);
    pauseTrack();
  };

  const onShareClick = (): void => {
    logClickEvent(SHARE, playablePandoraId);
    openShareModal(curatorPandoraId, {
      shareParentSourceId: curatorPandoraId,
      shareParentSourceType: D.PandoraTypes.CURATOR,
    });
  };

  return {
    playOnClick,
    pauseOnClick,
    onShareClick,
  };
}

export const mapStateToProps: MapStateToProps<
  ICuratorButtonControlsStateProps,
  ICuratorButtonControlsOwnProps,
  $TSFixMe
> = state => {
  return {
    showPremiumAccess: D.Utils.Listener.showPremiumAccess(state),
  };
};

const mapDispatchToProps: MapDispatchToProps<
  ICuratorButtonControlsDispatchProps,
  ICuratorButtonControlsOwnProps
> = {
  closeModal: D.closeModal,
  openModal: D.openModal,
  openShareModal: D.openShareModal,
  pauseTrack: D.pauseTrack,
  playResumeSource: D.playResumeSource,
};

export function withCuratorButtonControls(
  WrappedComponent: React.ComponentType<$TSFixMe>,
): $TSFixMe {
  const CuratorButtonControls: React.FC<ICuratorButtonControlsProps> = props => {
    const { playOnClick, pauseOnClick, onShareClick } = useClickControls(props);

    return (
      <WrappedComponent
        playOnClick={playOnClick}
        pauseOnClick={pauseOnClick}
        onShareClick={onShareClick}
        {...props}
      />
    );
  };

  // @ts-expect-error
  return connect(mapStateToProps, mapDispatchToProps)(CuratorButtonControls);
}
